/**
 * Public site content (contract v1.2 §3.11, §3.13).
 *
 * GET /api/public/content/<collection> returns the visible items of one
 * collection, ordered, with media references already expanded into media
 * objects (§3.13.2). The browser's HTTP cache handles the ETag / 304
 * revalidation (Cache-Control: max-age=60); nothing to do here.
 *
 * Callers keep a bundled copy of the seed (src/data/contentSeed/) and show
 * it when this call fails, so an API outage never blanks a page.
 *
 * @module contentService
 */

import { get } from './apiClient';
import ApiError, { ERROR_CODES } from '../errors/ApiError';
import { ERROR_MESSAGES } from '../errors/errorHandler';

/** Every content collection (contract §3.11). */
export const CONTENT_COLLECTIONS = Object.freeze([
  'groups',
  'workshops',
  'private_groups',
  'resources',
  'grounding',
  'downloads',
  'faqs',
  'settings',
  'gallery', // v1.3
]);

/**
 * Visible items of one collection.
 *
 * @param {string} collection - One of CONTENT_COLLECTIONS
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{collection: string, updated_at: string|null, items: Array<{slug: string, position: number, updated_at: string, data: object}>}>}
 * @throws {ApiError} 404 not_found (unknown collection), 429, network errors
 */
export const getContent = async (collection, { signal } = {}) => {
  const data = await get(`/api/public/content/${encodeURIComponent(collection)}`, { signal });
  if (!Array.isArray(data.items)) {
    throw new ApiError(ERROR_MESSAGES[ERROR_CODES.INVALID_RESPONSE], 200, ERROR_CODES.INVALID_RESPONSE);
  }
  return data;
};
